/**
 * Convert a UUID-like hex string back to its original UTF-8 string:
 * 1) Remove hyphens
 * 2) Trim leading zeros
 * 3) Hex-decode
 * 4) Return as UTF-8 string
 */
export function getUUIDConversion(uuidString: string): string {
  if (typeof uuidString !== "string") {
    throw new TypeError("uuidString must be a string")
  }

  // Remove hyphens and trim leading zeros
  let compact = uuidString.replace(/-/g, "").replace(/^0+/, "")

  if (compact.length === 0) {
    // In Go, hex.DecodeString("") returns []byte{} with no error.
    // Converting to string yields "", so we follow the same.
    return ""
  }

  // Re-pad to even length for hex decoding. Trimming leading zeros can
  // leave an odd nibble count (first byte < 0x10) — throwing here crashed
  // app boot (getAppConfig → navigateTo401) deterministically for any
  // tenant pair whose XOR starts with an odd number of zero nibbles.
  if (compact.length % 2 !== 0) {
    compact = "0" + compact
  }

  // Validate hex
  if (!/^[0-9a-fA-F]+$/.test(compact)) {
    throw new Error("invalid hex characters in input")
  }

  // Decode hex to bytes and return UTF-8 string (browser-compatible)
  const bytes = new Uint8Array(compact.length / 2)
  for (let i = 0; i < compact.length; i += 2) {
    bytes[i / 2] = parseInt(compact.substring(i, i + 2), 16)
  }
  const decoder = new TextDecoder("utf-8")
  return decoder.decode(bytes)
}

/**
 * Create a deterministic UUID from envID and companyID by XORing them together.
 * This ensures a fixed-length output regardless of input length.
 *
 * @param envID - First UUID (e.g., FW tenant ID)
 * @param companyID - Second UUID (e.g., SSE tenant ID)
 * @returns A new UUID created by XORing the two input UUIDs
 */
export function setUUIDConversion(envID: string, companyID: string): string {
  if (typeof envID !== "string" || typeof companyID !== "string") {
    throw new TypeError("envID and companyID must be strings")
  }

  // Remove hyphens from both UUIDs to get hex strings
  const env = envID.replace(/-/g, "")
  const company = companyID.replace(/-/g, "")

  // Ensure both are 32 hex characters (pad with zeros if needed)
  const envPadded = env.padEnd(32, "0")
  const companyPadded = company.padEnd(32, "0")

  // XOR the hex strings byte by byte to create a new UUID
  let result = ""
  for (let i = 0; i < 32; i += 2) {
    const envByte = parseInt(envPadded.substring(i, i + 2), 16)
    const companyByte = parseInt(companyPadded.substring(i, i + 2), 16)
    const xorByte = (envByte ^ companyByte).toString(16).padStart(2, "0")
    result += xorByte
  }

  // Format as canonical UUID: 8-4-4-4-12
  const uuid =
    `${result.substring(0, 8)}-` +
    `${result.substring(8, 12)}-` +
    `${result.substring(12, 16)}-` +
    `${result.substring(16, 20)}-` +
    `${result.substring(20)}`

  return uuid.toLowerCase()
}
